import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { computeFolderNextStatesList, updateFolderState } from '../actionCreators';
import { validateStateChange } from 'features/document/edit/documents/actionCreators';
import { RightsContext } from 'providers/RightsContext';
import { Grid } from '@mui/material';

const FolderStatesSelectionListContainer = ({ documentId, siteId, currentState, nextStates, maxLength, facilityId, actions, t }) => {
  const [selectedState, setSelectedState] = useState(null);
  const { readOnlyFull, readOnlyByDocState } = useContext(RightsContext);

  useEffect(() => {
    if (selectedState === null && currentState && currentState.stateId) setSelectedState({ ...currentState });
  }, [currentState, selectedState]);

  useEffect(() => {
    if (!nextStates.isLoaded) actions.computeFolderNextStatesList(documentId, siteId);
  }, [documentId, siteId, nextStates.isLoaded, actions]);

  const onStateChangeValidatedCallback = nextSelectedState => {
    if (
      nextSelectedState.stateId === selectedState?.stateId ||
      (nextSelectedState.requiresConfirmation && !window.confirm(t('ConfirmStateChange')))
    )
      return;
    actions.updateFolderState(documentId, siteId, nextSelectedState?.stateId, facilityId, () => setSelectedState(nextSelectedState));
  };

  const handleSelectState = nextSelectedState => {
    actions.validateStateChange(documentId, siteId, selectedState?.stateId, nextSelectedState?.stateId, () =>
      onStateChangeValidatedCallback(nextSelectedState)
    );
  };

  const optionEqualsValueAutocomplete = useCallback((option, value) => option.stateId === value?.stateId, []);

  return (
    <Grid item xs='auto'>
      <Autocomplete
        valueKey='stateId'
        labelKey='stateName'
        value={currentState}
        onChange={handleSelectState}
        sx={{ minWidth: `${maxLength * 8}px` }}
        simpleValue={false}
        isClearable={false}
        isSearchable={false}
        options={nextStates}
        disabled={readOnlyFull && !readOnlyByDocState}
        isOptionEqualToValue={optionEqualsValueAutocomplete}
      />
    </Grid>
  );
};

FolderStatesSelectionListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  currentState: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  nextStates: PropTypes.array.isRequired,
  maxLength: PropTypes.number,
  facilityId: PropTypes.number,
  actions: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    currentState: { stateId: state.currentFolder.header.stateId, stateName: state.currentFolder.header.stateName },
    nextStates: state.currentFolder.nextStates,
    maxLength: state.currentFolder.nextStates?.reduce((max, elem) => Math.max(max, elem.stateName.length), 0),
    facilityId: state.currentFolder.header.facilityId
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        computeFolderNextStatesList,
        updateFolderState,
        validateStateChange
      },
      dispatch
    )
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(FolderStatesSelectionListContainer);
